import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Political meme/emoji data
const POLITICAL_EMOJIS = {
  democrat: [
    { code: "🔵", name: "Blue Wave" },
    { code: "🫏", name: "Democrat Donkey" },
    { code: "🗽", name: "Statue of Liberty" },
    { code: "🌊", name: "Blue Wave" },
    { code: "✊", name: "Solidarity" },
    { code: "🏳️‍🌈", name: "Pride" },
    { code: "🌍", name: "Climate" },
    { code: "💙", name: "Blue Heart" },
  ],
  republican: [
    { code: "🔴", name: "Red State" },
    { code: "🐘", name: "GOP Elephant" },
    { code: "🦅", name: "Eagle" },
    { code: "⭐", name: "Star" },
    { code: "🇺🇸", name: "USA Flag" },
    { code: "💪", name: "Strong" },
    { code: "🔫", name: "2nd Amendment" },
    { code: "❤️", name: "Red Heart" },
  ],
  trump: [
    { code: "🍊", name: "Orange Man" },
    { code: "🧱", name: "The Wall" },
    { code: "🏌️", name: "Golf" },
    { code: "🤡", name: "Clown" },
    { code: "💰", name: "Money Bags" },
    { code: "🎺", name: "Trump-et" },
    { code: "👐", name: "Big Hands" },
    { code: "🔶", name: "Orange" },
    { code: "📺", name: "Fake News" },
    { code: "🏛️", name: "White House" },
  ],
  biden: [
    { code: "😴", name: "Sleepy Joe" },
    { code: "🍦", name: "Ice Cream" },
    { code: "👴", name: "Old Man" },
    { code: "🕶️", name: "Aviators" },
    { code: "🚂", name: "Amtrak Joe" },
    { code: "🤝", name: "Unity" },
    { code: "💊", name: "Rx Costs" },
    { code: "⚡", name: "Electric Cars" },
  ],
  general: [
    { code: "🗳️", name: "Vote" },
    { code: "📊", name: "Polls" },
    { code: "📰", name: "News" },
    { code: "🎤", name: "Debate" },
    { code: "🏛️", name: "Congress" },
    { code: "⚖️", name: "Justice" },
    { code: "💵", name: "Economy" },
    { code: "🎪", name: "Circus" },
    { code: "🤦", name: "Facepalm" },
    { code: "🍿", name: "Popcorn" },
    { code: "🔥", name: "Hot Take" },
    { code: "💀", name: "Dead" },
  ]
};

const POLITICAL_PHRASES = [
  "Make America Great Again! 🇺🇸",
  "Build Back Better! 💙",
  "Lock them up! 🔒",
  "No malarkey! 🍦",
  "Fake News! 📺",
  "Here's the deal... 🕶️",
  "Wrong! ❌",
  "C'mon man! 😤",
  "SAD! 😢",
  "Let's go Brandon! 🏎️",
  "Dark Brandon rises! 😎",
  "Covfefe ☕",
  "Bigly! 👐",
  "Vote Blue! 🔵",
  "Vote Red! 🔴",
];

const PoliticalFightPanel = ({ roomId, onInsertEmoji }) => {
  const [activeTab, setActiveTab] = useState("general");
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Only show in political fight room
  if (roomId !== "political-fight-room") return null;

  const insertEmoji = (emoji) => {
    if (onInsertEmoji) {
      onInsertEmoji(emoji);
    }
  };

  const insertPhrase = (phrase) => {
    if (onInsertEmoji) {
      onInsertEmoji(phrase);
    }
  };

  // Collapsed state - just show a compact toggle button
  if (!isExpanded) {
    return (
      <div className="flex-shrink-0" data-testid="political-panel-collapsed">
        <button
          onClick={() => setIsExpanded(true)}
          className="retro-button flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-red-400 via-white to-blue-400 hover:from-red-300 hover:to-blue-300 text-xs font-bold"
          title="Open Political Arsenal"
        >
          <ChevronRight className="w-3 h-3" />
          🏛️ Political Arsenal
        </button>
      </div>
    );
  }

  // Expanded state - show as a side panel overlay
  return (
    <div 
      className="absolute right-0 top-0 h-full w-64 z-40 shadow-lg"
      data-testid="political-panel"
    >
      <div className="retro-window h-full flex flex-col">
        {/* Header with collapse button */}
        <div className="retro-title-bar px-2 py-1 flex items-center justify-between">
          <span className="text-xs">🏛️ POLITICAL ARSENAL</span>
          <button
            onClick={() => setIsExpanded(false)}
            className="hover:bg-white/20 px-1 rounded"
            title="Collapse"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
        </div>

        <div className="flex-1 p-2 bg-[#C0C0C0] overflow-y-auto retro-scrollbar">
          {/* Compact Warning Banner */}
          <div className="bg-gradient-to-r from-red-500 via-white to-blue-500 p-1.5 mb-2 rounded text-center">
            <p className="text-[10px] font-bold text-gray-800">
              ⚠️ DEBATE ZONE 🔥
            </p>
          </div>

          {/* Party Tabs - Vertical for side panel */}
          <div className="grid grid-cols-3 gap-1 mb-2">
            <button
              className={`retro-button text-[9px] px-1 py-0.5 ${activeTab === 'general' ? 'bg-gray-300' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              🎪
            </button>
            <button
              className={`retro-button text-[9px] px-1 py-0.5 ${activeTab === 'democrat' ? 'bg-blue-200' : ''}`}
              onClick={() => setActiveTab('democrat')}
            >
              🔵
            </button>
            <button
              className={`retro-button text-[9px] px-1 py-0.5 ${activeTab === 'republican' ? 'bg-red-200' : ''}`}
              onClick={() => setActiveTab('republican')}
            >
              🔴
            </button>
            <button
              className={`retro-button text-[9px] px-1 py-0.5 ${activeTab === 'trump' ? 'bg-orange-200' : ''}`}
              onClick={() => setActiveTab('trump')}
            >
              🍊
            </button>
            <button
              className={`retro-button text-[9px] px-1 py-0.5 ${activeTab === 'biden' ? 'bg-blue-100' : ''}`}
              onClick={() => setActiveTab('biden')}
            >
              🍦
            </button>
          </div>

          {/* Tab Label */}
          <p className="text-[10px] font-bold text-center mb-1 text-gray-600">
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Emojis
          </p>

          {/* Emoji Grid - Compact */}
          <div className="retro-inset p-1.5 bg-white mb-2">
            <div className="grid grid-cols-4 gap-0.5">
              {POLITICAL_EMOJIS[activeTab]?.map((emoji, i) => (
                <button
                  key={i}
                  className="text-lg hover:bg-gray-100 p-0.5 rounded cursor-pointer"
                  onClick={() => insertEmoji(emoji.code)}
                  title={emoji.name}
                >
                  {emoji.code}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Phrases - Scrollable */}
          <div className="retro-inset p-1.5 bg-white">
            <p className="text-[9px] font-bold text-gray-600 mb-1">Quick Phrases:</p>
            <div className="space-y-0.5 max-h-32 overflow-y-auto">
              {POLITICAL_PHRASES.map((phrase, i) => (
                <button
                  key={i}
                  className="retro-button text-[9px] px-1 py-0.5 w-full text-left hover:bg-yellow-100 truncate"
                  onClick={() => insertPhrase(phrase)}
                  title={phrase}
                >
                  {phrase.length > 25 ? phrase.substring(0, 23) + '...' : phrase}
                </button>
              ))}
            </div>
          </div>

          {/* Debate Rules */}
          <div className="mt-2 text-[8px] text-gray-500 text-center">
            🏛️ Keep it fun! No threats.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliticalFightPanel;
